import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

function Portfolio() {
  const [companyLanguage, setCompanyLanguage] = useState(() => {
    return localStorage.getItem("companyLanguage") || 'en';
  });
  const [filter, setFilter] = useState(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    return lang === 'ur' ? "سب" : "All";
  });

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
      setFilter(updatedLang === 'ur' ? "سب" : "All");
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
        setFilter(updatedLang === 'ur' ? "سب" : "All");
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  const projects = [
    { id: 1, title: "Event Management Platform", category: "Empower", type: "Web" },
    { id: 2, title: "Digital Marketing Campaign", category: "Support", type: "Marketing" },
    { id: 3, title: "Interactive Learning Platform", category: "Business", type: "Web" },
    { id: 4, title: "Environmental Impact Dashboard", category: "%", type: "Data" },
    { id: 5, title: "E-Commerce Solution", category: "Retail", type: "Web" },
    { id: 6, title: "Mobile Banking App", category: "Finance", type: "Mobile" },
    { id: 7, title: "Healthcare Management System", category: "Health", type: "Web" },
    { id: 8, title: "Supply Chain Optimization", category: "Logistics", type: "Data" }
  ];

  const categories = companyLanguage === 'ur' 
    ? ["سب", "ویب", "مارکیٹنگ", "ڈیٹا", "موبائل"]
    : ["All", "Web", "Marketing", "Data", "Mobile"];


  const filteredProjects = (filter === "All" || filter === "سب")
    ? projects 
    : projects.filter(p => {
        if (companyLanguage === 'ur') {
          const typeMap = { "ویب": "Web", "مارکیٹنگ": "Marketing", "ڈیٹا": "Data", "موبائل": "Mobile" };
          return p.type === typeMap[filter];
        }
        return p.type === filter;
      });

  return (
    <>
      <Helmet><title>Portfolio - Bexon Business Solutions</title></Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4">
            {companyLanguage === 'ur' ? 'ہمارا پورٹ فولیو' : 'Our Portfolio'}
          </h1>
          <p className="text-xl text-gray-300">
            {companyLanguage === 'ur' ? 'حدود توڑنا، خواب بنانا' : 'Breaking Boundaries, Building Dreams'}
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-2 rounded-lg font-semibold transition ${
                  filter === cat
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredProjects.map((project) => (
              <Link
                key={project.id}
                to={`/portfolio/${project.id}`}
                className="bg-gray-50 p-6 rounded-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="text-4xl font-bold text-indigo-600 mb-3 group-hover:scale-110 transition">
                  {project.category}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{project.title}</h3>
                <p className="text-sm text-gray-600">{project.type}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-indigo-600 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">%</div>
              <p className="text-gray-200">
                {companyLanguage === 'ur' ? 'منصوبے مکمل' : 'Projects Completed'}
              </p>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">M</div>
              <p className="text-gray-200">
                {companyLanguage === 'ur' ? 'دنیا بھر میں پہنچ' : 'Reach Worldwide'}
              </p>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">X</div>
              <p className="text-gray-200">
                {companyLanguage === 'ur' ? 'تیز ترقی' : 'Faster Growth'}
              </p>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">+</div>
              <p className="text-gray-200">
                {companyLanguage === 'ur' ? 'ایوارڈز محفوظ' : 'Awards Archived'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            {companyLanguage === 'ur' ? 'ذہن میں کوئی منصوبہ ہے؟' : 'Have a Project in Mind?'}
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            {companyLanguage === 'ur' ? 'آئیں مل کر آپ کے خواب کو حقیقت بنائیں۔' : "Let's work together to bring your vision to life."}
          </p>
          <Link to="/contact" className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded-lg transition inline-block">
            {companyLanguage === 'ur' ? 'اپنا منصوبہ شروع کریں' : 'Start Your Project'}
          </Link>
        </div>
      </section>
    </>
  );
}

export default Portfolio;

